"use client";
import { ToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "../../utils";
import { Icon, type IconName } from "../Icon";
import { DisabledTooltipTrigger } from "../Tooltip/DisabledTooltipTrigger";

export type SegmentOption = {
  id: string;
  label: string;
  icon?: IconName;
  disabledReason?: string | undefined;
  truncate?: boolean | undefined;
};

type SegmentedControlVariant = "primary" | "subtle";
type LegacySegmentedControlVariant = "default" | "brand" | "secondary";

function normalizeSegmentedControlVariant(
  variant: SegmentedControlVariant | LegacySegmentedControlVariant | undefined
): SegmentedControlVariant {
  if (variant === "brand") return "primary";
  if (variant === "default" || variant === "secondary" || !variant) return "subtle";
  return variant;
}

export interface SegmentedControlProps {
  /**
   * Array of segment options
   */
  options: SegmentOption[];
  /**
   * Currently selected value
   */
  value: string;
  /**
   * Callback when selection changes
   */
  onChange: (value: string) => void;
  /**
   * Size of the control
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether the control is disabled
   */
  isDisabled?: boolean;
  /**
   * Aria label for accessibility
   */
  "aria-label"?: string;
  /**
   * Visual variant of the control
   * @default "subtle"
   */
  variant?: SegmentedControlVariant | LegacySegmentedControlVariant;
  /**
   * When true, segments render only their icon — labels become sr-only and
   * still drive `aria-label` for assistive tech. Each option must provide an
   * `icon`; options without one fall back to showing the label.
   * @default false
   */
  iconOnly?: boolean;
}

const sizeStyles = {
  sm: "h-[var(--control-sm-height)] text-xs",
  md: "h-[var(--control-md-height)] text-sm",
  lg: "h-[var(--control-lg-height)] text-base",
  xl: "h-[var(--control-xl-height)] text-lg",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

const getButtonClass = ({
  isSelected,
  isFirst: _isFirst,
  isLast,
  isDisabled,
  variant = "subtle",
}: {
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  isDisabled?: boolean;
  variant?: SegmentedControlVariant | LegacySegmentedControlVariant;
}) => {
  const normalizedVariant = normalizeSegmentedControlVariant(variant);
  const focusRingColor =
    normalizedVariant === "primary"
      ? "focus-visible:outline-action-primary"
      : "focus-visible:outline-[color:var(--color-action-secondary-text)]";

  // Subtle variant: lighter, lower-emphasis styling
  if (normalizedVariant === "subtle") {
    return twMerge(
      // Base styles
      focusRing(),
      "focus-visible:outline-[color:var(--color-action-secondary-text)]",
      "px-3 text-center transition cursor-pointer forced-color-adjust-none -ml-px first:ml-0 flex items-center justify-center h-full gap-2",

      // Selection state - light gray background for clear distinction
      isSelected
        ? "bg-background-muted text-text-primary font-medium forced-colors:!bg-[Highlight] forced-colors:!text-[HighlightText]"
        : twMerge(
            "text-text-secondary forced-colors:!bg-[ButtonFace] forced-colors:!text-[ButtonText]",
            !isDisabled && "hover:bg-background-hover pressed:bg-background-hover"
          ),

      // Divider
      !isLast && "border-r border-border-muted",

      // Disabled state
      isDisabled &&
        "bg-background-muted text-text-disabled forced-colors:!bg-[ButtonFace] forced-colors:!text-[GrayText] border-border-muted forced-colors:border-[GrayText] cursor-not-allowed"
    );
  }

  // Primary variant: bold styling
  return twMerge(
    // Base styles
    focusRing(),
    focusRingColor,
    "px-3 text-center transition cursor-pointer forced-color-adjust-none -ml-px first:ml-0 flex items-center justify-center h-full gap-2",

    // Selection state
    isSelected
      ? "bg-action-primary text-[color:var(--color-action-primary-text)] font-semibold forced-colors:!bg-[Highlight] forced-colors:!text-[HighlightText]"
      : twMerge(
          "text-text-secondary forced-colors:!bg-[ButtonFace] forced-colors:!text-[ButtonText]",
          !isDisabled && "hover:bg-background-hover pressed:bg-background-hover hover:font-semibold"
        ),

    // Divider
    !isLast && "border-r border-border-muted",

    // Disabled state
    isDisabled &&
      "bg-background-muted text-text-disabled forced-colors:!bg-[ButtonFace] forced-colors:!text-[GrayText] border-border-muted forced-colors:border-[GrayText] cursor-not-allowed"
  );
};

/**
 * SegmentedControl
 *
 * A segmented control component for selecting between multiple options.
 * Similar to a radio group but with a more compact, button-like appearance.
 */
export function SegmentedControl({
  options,
  value,
  onChange,
  size = "md",
  className,
  isDisabled,
  variant = "subtle",
  iconOnly = false,
  "aria-label": ariaLabel,
}: SegmentedControlProps) {
  const canShrink = options.some((option) => option.truncate);
  return (
    <div
      className={twMerge(
        "inline-flex overflow-hidden rounded-[var(--control-border-radius)]",
        canShrink && "max-w-full",
        // Subtle variant uses thinner border
        normalizeSegmentedControlVariant(variant) === "subtle"
          ? "border border-border-muted"
          : "border-2 border-border-muted",
        sizeStyles[size],
        className
      )}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option, index) => {
        const hideLabel = iconOnly && Boolean(option.icon);
        const isSelected = value === option.id;
        const position = { isFirst: index === 0, isLast: index === options.length - 1, variant };
        const fit = option.truncate ? "min-w-0" : canShrink && "shrink-0";
        const content = (
          <>
            {option.icon && <Icon name={option.icon} size={iconSizes[size]} />}
            {hideLabel ? (
              <span className="sr-only">{option.label}</span>
            ) : (
              <span
                className={twMerge(
                  "inline-flex flex-col items-center after:content-[attr(data-label)] after:font-semibold after:h-0 after:overflow-hidden after:invisible",
                  option.truncate && "min-w-0 max-w-full"
                )}
                data-label={option.label}
              >
                {option.truncate ? (
                  <span className="max-w-full truncate" title={option.label}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )}
              </span>
            )}
          </>
        );
        if (option.disabledReason) {
          return (
            <DisabledTooltipTrigger
              key={option.id}
              reason={option.disabledReason}
              aria-label={option.label}
              aria-pressed={isSelected}
              className={twMerge(getButtonClass({ ...position, isSelected, isDisabled: true }), "bg-transparent", fit)}
            >
              {content}
            </DisabledTooltipTrigger>
          );
        }
        return (
          <ToggleButton
            key={option.id}
            isSelected={isSelected}
            onChange={() => onChange(option.id)}
            className={twMerge(getButtonClass({ ...position, isSelected, isDisabled }), fit)}
            isDisabled={isDisabled}
            aria-label={option.label}
          >
            {content}
          </ToggleButton>
        );
      })}
    </div>
  );
}
